import { ReactElement, useCallback } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { FANCY_CONTAINER_CLASS_NAME } from '@components/icon-fancy-container/icon-fancy-container';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconDrag } from '@components/icons/drag';
import { IconJob } from '@components/icons/job';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { Menu } from '@components/menu';
import { Nav } from '@components/nav';
import { NavGroupCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { useNavContext } from '@components/nav/nav';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';
import { pxToRem } from '@utils/converters';

import {
  useAddFavorite,
  useCachedFavorites,
  useFavoritesOrdered,
  useRemoveFavorite,
} from 'api/favorites/hooks';
import { FavoriteContentType, UserFavorite } from 'api/favorites/requests';
import { useUserOption } from 'features/authentication';

const contentTypeMap: Record<
  FavoriteContentType,
  {
    getLink: (props: { objectId: number; jobId: number }) => string;
    icon: ReactElement<IconProps>;
  }
> = {
  application: {
    getLink: ({ objectId, jobId }) => {
      return `/jobs/${jobId}/application/${objectId}/details`;
    },
    icon: <IconApplicants size={16} />,
  },
  job: {
    getLink: ({ objectId }) => {
      return `/jobs/${objectId}/`;
    },
    icon: <IconJob size={16} />,
  },
};

const generateLinkItem = ({ name, contentType, id, jobId, objectId }: UserFavorite) => {
  const { getLink, icon } = contentTypeMap[contentType];

  return {
    href: getLink({ objectId, jobId }),
    icon: (
      <IconFancyContainer
        size="sm"
        icon={icon}
        hoverIcon={<IconDrag size={16} color="white100" />}
        variant="gray"
      />
    ),
    label: name,
    id: String(id),
    iconRight: (
      <Menu
        trigger={IconButton}
        triggerProps={{
          size: 'sm',
          variant: 'ghost-purple',
          icon: <IconMoreVertical size={16} />,
        }}
      >
        <RemoveFavoriteMenuItem id={id} />
      </Menu>
    ),
  };
};

type FavoriteMenuItemProps = Pick<UserFavorite, 'id' | 'contentType' | 'objectId'>;

export const RemoveFavoriteMenuItem = ({ id }: Pick<FavoriteMenuItemProps, 'id'>) => {
  const { t } = useScopedTranslate(locale);
  const removeFromFavorites = useRemoveFavorite();
  return (
    <Menu.Item
      onSelect={() => removeFromFavorites.mutate(id)}
      dataCy="remove-from-favorites"
      label={t('removeFromFavorites')}
      icon={<IconStarSolid color="orange100" />}
    />
  );
};

const expandedStore = createSelectionStore({ persistKey: 'favorites-nav-state' });

export const Favorites = () => {
  const { t } = useScopedTranslate(locale);
  const favorites = useFavoritesOrdered();
  const { update } = useUserOption('navbarFavoritesOrder', []);
  const { isOpen } = useNavContext();

  if (!favorites?.length) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <IconFancyContainer icon={<IconStarSolid size={16} color="orange100" />} variant="gray" />
      )}
      <Container>
        <NavGroupCollapse
          id="main-navbar-favorites"
          label={t('favorites')}
          links={favorites.map(generateLinkItem)}
          toggleGroup={expandedStore.toggle}
          expandedSelectionStore={expandedStore}
          onDragEnd={update.mutate}
        />
        <Nav.Separator />
      </Container>
    </>
  );
};

const getFavoriteItem = (favorites: UserFavorite[], objectId: number, contentType: string) => {
  return favorites.find(
    (favoriteItem) => favoriteItem.objectId === objectId && favoriteItem.contentType === contentType
  );
};

export const useFavoriteActionItem = (contentType: FavoriteContentType) => {
  const { t } = useScopedTranslate(locale);
  const removeFromFavorites = useRemoveFavorite();
  const addToFavorites = useAddFavorite();
  const cachedFavorites = useCachedFavorites();

  return useCallback(
    (objectId: number) => {
      const props = { contentType, objectId };
      const favoriteItem = getFavoriteItem(cachedFavorites, objectId, contentType);

      return favoriteItem
        ? {
            label: t('removeFromFavorites'),
            icon: <IconStarSolid color="orange100" />,
            onSelect: () => removeFromFavorites.mutate(favoriteItem.id),
          }
        : {
            label: t('addToFavorites'),
            icon: <IconStar />,
            onSelect: () => addToFavorites.mutate(props),
          };
    },
    [contentType, t, removeFromFavorites, addToFavorites, cachedFavorites]
  );
};

type FavoriteToggleProps = { contentType: FavoriteContentType; objectId: number };

export const FavoriteToggle = ({ contentType, objectId }: FavoriteToggleProps) => {
  const { t } = useScopedTranslate(locale);
  const removeFromFavorites = useRemoveFavorite();
  const addToFavorites = useAddFavorite();
  const cachedFavorites = useCachedFavorites();
  const favoriteItem = getFavoriteItem(cachedFavorites, objectId, contentType);

  return (
    <Tooltip content={favoriteItem ? t('removeFromFavorites') : t('addToFavorites')}>
      <FavoriteToggleContainer>
        {favoriteItem ? (
          <IconStarSolid
            color="orange100"
            onClick={() => removeFromFavorites.mutate(favoriteItem.id)}
          />
        ) : (
          <IconStar
            onClick={() => addToFavorites.mutate({ contentType, objectId })}
            color="buttonIconPositive"
          />
        )}
      </FavoriteToggleContainer>
    </Tooltip>
  );
};

const Container = styled.div({
  [`.${FANCY_CONTAINER_CLASS_NAME}`]: {
    height: pxToRem(16),
    width: pxToRem(16),
  },
  '.nav--icon': {
    display: 'flex',
    alignItems: 'center',
    minWidth: pxToRem(24),
  },
});

const FavoriteToggleContainer = styled.div({
  cursor: 'pointer',
});

const locale = {
  en: {
    favorites: 'Favourites',
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
  },
  is: {
    favorites: 'Uppáhalds',
    addToFavorites: 'Bæta við uppáhalds',
    removeFromFavorites: 'Fjarlægja úr uppáhalds',
  },
};
